import React, { useEffect, useState } from 'react';
import { tasksActionsService } from './tasksActionsService';
import { CentralTask, GovernanceAction, TaskMetrics } from './types';
import { useDialog } from '../../shared/DialogContext';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const TasksActionsPage: React.FC = () => {
  const { showError, showSuccess } = useDialog();
  const [tasks, setTasks] = useState<CentralTask[]>([]);
  const [actions, setActions] = useState<GovernanceAction[]>([]);
  const [metrics, setMetrics] = useState<TaskMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'queue' | 'actions' | 'tasks'>('queue');

  // Sorting for tasks (default to due_at desc or task_number desc)
  const {
    sortedItems: sortedTasks,
    sortField: taskSortField,
    sortDirection: taskSortOrder,
    toggleSort: handleTaskSort,
  } = useTableSort(tasks);

  // Sorting for actions (default to date desc or action_number desc)
  const {
    sortedItems: sortedActions,
    sortField: actionSortField,
    sortDirection: actionSortOrder,
    toggleSort: handleActionSort,
  } = useTableSort(actions);

  const {
    currentPage: actPage,
    setCurrentPage: setActPage,
    pageSize: actPageSize,
    setPageSize: setActPageSize,
    totalItems: totalActItems,
    paginatedItems: paginatedActions,
  } = useTablePagination(sortedActions, 25);

  const {
    currentPage: tskPage,
    setCurrentPage: setTskPage,
    pageSize: tskPageSize,
    setPageSize: setTskPageSize,
    totalItems: totalTskItems,
    paginatedItems: paginatedTasks,
  } = useTablePagination(sortedTasks, 25);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tskList, actList, mtr] = await Promise.all([
        tasksActionsService.getTasks(),
        tasksActionsService.getActions(),
        tasksActionsService.getMetrics(),
      ]);
      setTasks(tskList);
      setActions(actList);
      setMetrics(mtr);
    } catch (err) {
      console.error('Failed to load tasks and actions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await tasksActionsService.completeTask(taskId, 'Task execution completed by assigned personnel.');
      await loadData();
      showSuccess('Task marked as completed.');
    } catch (err: any) {
      showError(err, 'Task Completion Failed');
    }
  };

  const handleVerifyAction = async (actionId: string) => {
    try {
      await tasksActionsService.verifyAction(actionId, 'Lead Quality Auditor', 'PASS', 'Physical verification evidence verified.');
      await loadData();
      showSuccess('Action verified successfully.');
    } catch (err: any) {
      showError(err, 'Verification Failed');
    }
  };

  return (
    <div>
      {/* 1. FlowDesk Hero Header Banner */}
      <div className="page-header-banner">
        <div className="badge">OPERATIONS & GOVERNANCE</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Tasks & Actions Management</h1>
            <p>
              Centralized work execution queue, 3-tier outcome governance (Implemented ≠ Verified ≠ Effective), and CAPA resolution tracking.
            </p>
          </div>
          <button
            onClick={loadData}
            className="btn-secondary"
            style={{ width: 'auto' }}
          >
            Refresh Worklist
          </button>
        </div>
      </div>

      {/* 2. KPI Metrics Cards */}
      {metrics && (
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-label">Active Tasks</span>
            <span className="stat-value" style={{ color: '#2563eb' }}>{metrics.myTasksCount}</span>
            <span className="stat-sub">Operational Execution Units</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Governance Actions</span>
            <span className="stat-value" style={{ color: '#7c3aed' }}>{metrics.myActionsCount}</span>
            <span className="stat-sub">Outcome Audit Records</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Due Soon (7 Days)</span>
            <span className="stat-value" style={{ color: '#d97706' }}>{metrics.dueSoonCount}</span>
            <span className="stat-sub">High Priority Target</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Overdue Work</span>
            <span className="stat-value" style={{ color: '#dc2626' }}>
              {metrics.overdueTasksCount + metrics.overdueActionsCount}
            </span>
            <span className="stat-sub">Immediate Escalation Required</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Completed Units</span>
            <span className="stat-value" style={{ color: '#059669' }}>{metrics.completedCount}</span>
            <span className="stat-sub">Verified & Closed Work</span>
          </div>
        </div>
      )}

      {/* 3. Navigation Tabs */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveTab('queue')}
          className={`tab-btn ${activeTab === 'queue' ? 'active' : ''}`}
        >
          📋 Priority Work Queue
        </button>
        <button
          onClick={() => setActiveTab('actions')}
          className={`tab-btn ${activeTab === 'actions' ? 'active' : ''}`}
        >
          🎯 CAPA & Governance Actions ({actions.length})
        </button>
        <button
          onClick={() => setActiveTab('tasks')}
          className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
        >
          ⚡ Tasks List ({tasks.length})
        </button>
      </div>

      <div>
          {/* TAB 1: CENTRAL QUEUE */}
          {activeTab === 'queue' && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
              <div className="card" style={{ marginBottom: 0 }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>
                  Central Task Worklist
                </h3>
                <div className="table-container" style={{ marginBottom: 0 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <SortableTh field="task_number" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                          Code
                        </SortableTh>
                        <SortableTh field="title" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                          Title
                        </SortableTh>
                        <SortableTh field="priority" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                          Priority
                        </SortableTh>
                        <SortableTh field="due_at" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                          Due Date
                        </SortableTh>
                        <SortableTh field="status" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                          Status
                        </SortableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTasks.map((t) => (
                        <tr key={t.id}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>
                            {t.task_number}
                          </td>
                          <td style={{ fontWeight: 600, color: '#0f172a' }}>{t.title}</td>
                          <td>
                            <span className={`badge-pill ${
                              t.priority === 'HIGH' || t.priority === 'CRITICAL' ? 'badge-danger' : 'badge-neutral'
                            }`}>
                              {t.priority}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px', color: '#64748b' }}>{t.due_at?.split(' ')[0]}</td>
                          <td>
                            <span className={`badge-pill ${t.status === 'COMPLETED' ? 'badge-success' : 'badge-warning'}`}>
                              {t.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Architectural Principles Card */}
              <div className="card" style={{ marginBottom: 0, background: '#f8fafc' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
                  💡 Three-Tier Outcome Separation
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '12px', color: '#475569' }}>
                  <div style={{ borderLeft: '3px solid #0284c7', paddingLeft: '10px' }}>
                    <strong style={{ color: '#0f172a', display: 'block', marginBottom: '2px' }}>1. IMPLEMENTED</strong>
                    Tasks completed, modified operational manual published, or physical GSE equipment updated.
                  </div>
                  <div style={{ borderLeft: '3px solid #7c3aed', paddingLeft: '10px' }}>
                    <strong style={{ color: '#0f172a', display: 'block', marginBottom: '2px' }}>2. VERIFIED</strong>
                    Independent qualified auditor checks that the implementation adheres to requirements and safety standards.
                  </div>
                  <div style={{ borderLeft: '3px solid #059669', paddingLeft: '10px' }}>
                    <strong style={{ color: '#0f172a', display: 'block', marginBottom: '2px' }}>3. EFFECTIVE</strong>
                    Follow-up verification confirms that recurrence was eliminated and target safety performance indicators (SPI) were met.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: ACTIONS REGISTER */}
          {activeTab === 'actions' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortableTh field="action_number" currentField={actionSortField} currentDirection={actionSortOrder} onSort={handleActionSort}>
                      Action No
                    </SortableTh>
                    <SortableTh field="title" currentField={actionSortField} currentDirection={actionSortOrder} onSort={handleActionSort}>
                      Action Title
                    </SortableTh>
                    <SortableTh field="source_type" currentField={actionSortField} currentDirection={actionSortOrder} onSort={handleActionSort}>
                      Source Reference
                    </SortableTh>
                    <SortableTh field="criticality" currentField={actionSortField} currentDirection={actionSortOrder} onSort={handleActionSort}>
                      Criticality
                    </SortableTh>
                    <SortableTh field="progress_percent" currentField={actionSortField} currentDirection={actionSortOrder} onSort={handleActionSort}>
                      Progress
                    </SortableTh>
                    <SortableTh field="status" currentField={actionSortField} currentDirection={actionSortOrder} onSort={handleActionSort}>
                      Status
                    </SortableTh>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedActions.map((act) => (
                    <tr key={act.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>
                        {act.action_number}
                      </td>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{act.title}</td>
                      <td>
                        <span className="badge-pill badge-neutral">
                          {act.source_type} ({act.source_id})
                        </span>
                      </td>
                      <td>
                        <span className={`badge-pill ${
                          act.criticality === 'HIGH' || act.criticality === 'CRITICAL' ? 'badge-danger' : 'badge-neutral'
                        }`}>
                          {act.criticality}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontWeight: 600, fontSize: '12px' }}>{act.progress_percent}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge-pill ${
                          act.status === 'VERIFIED' || act.status === 'EFFECTIVE'
                            ? 'badge-success'
                            : act.status === 'IMPLEMENTED'
                            ? 'badge-info'
                            : 'badge-warning'
                        }`}>
                          {act.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {act.status === 'IMPLEMENTED' && (
                          <button
                            onClick={() => handleVerifyAction(act.id)}
                            className="btn-primary"
                            style={{ width: 'auto', padding: '4px 12px', fontSize: '12px', background: '#7c3aed' }}
                          >
                            Verify Action
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination
                currentPage={actPage}
                totalItems={totalActItems}
                pageSize={actPageSize}
                onPageChange={setActPage}
                onPageSizeChange={setActPageSize}
                itemLabel="actions"
              />
            </div>
          )}

          {/* TAB 3: TASKS */}
          {activeTab === 'tasks' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortableTh field="task_number" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                      Task No
                    </SortableTh>
                    <SortableTh field="title" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                      Task Title
                    </SortableTh>
                    <SortableTh field="task_type" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                      Type
                    </SortableTh>
                    <SortableTh field="priority" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                      Priority
                    </SortableTh>
                    <SortableTh field="due_at" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                      Due Date
                    </SortableTh>
                    <SortableTh field="status" currentField={taskSortField} currentDirection={taskSortOrder} onSort={handleTaskSort}>
                      Status
                    </SortableTh>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTasks.map((tsk) => (
                    <tr key={tsk.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>
                        {tsk.task_number}
                      </td>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{tsk.title}</td>
                      <td>
                        <span className="badge-pill badge-neutral">{tsk.task_type}</span>
                      </td>
                      <td>
                        <span className={`badge-pill ${
                          tsk.priority === 'HIGH' || tsk.priority === 'CRITICAL' ? 'badge-danger' : 'badge-neutral'
                        }`}>
                          {tsk.priority}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>{tsk.due_at?.split(' ')[0]}</td>
                      <td>
                        <span className={`badge-pill ${tsk.status === 'COMPLETED' ? 'badge-success' : 'badge-warning'}`}>
                          {tsk.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {tsk.status !== 'COMPLETED' && (
                          <button
                            onClick={() => handleCompleteTask(tsk.id)}
                            className="btn-primary"
                            style={{ width: 'auto', padding: '4px 12px', fontSize: '12px', background: '#10b981' }}
                          >
                            Mark Completed
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination
                currentPage={tskPage}
                totalItems={totalTskItems}
                pageSize={tskPageSize}
                onPageChange={setTskPage}
                onPageSizeChange={setTskPageSize}
                itemLabel="tasks"
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default TasksActionsPage;
